// src/components/SubmitTime/fields/RichTextEditor.tsx
// Modern React implementation of RichTextEditor custom field using Tiptap
// Replaces jQuery Summernote with modern Tiptap editor

import React, { useState, useEffect } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import TextAlign from '@tiptap/extension-text-align';
import { TextStyle } from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';
// TODO: FontFamily extension has compatibility issues with TipTap v3.26.0
// import FontFamily from '@tiptap/extension-font-family';
import Placeholder from '@tiptap/extension-placeholder';
import type { CustomFieldProps } from '../types';
import './RichTextEditor.css';

interface RichTextEditorProps extends CustomFieldProps {}

export const RichTextEditor: React.FC<RichTextEditorProps> = ({
  customField,
  canEdit,
  handleDynamicInputChange,
  UpdateCustomFields,
  showRequiredError,
}) =>
{
  const [currentColor, setCurrentColor] = useState('#000000');
  const [showColorPicker, setShowColorPicker] = useState(false);

  // Initialize Tiptap editor
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3, 4, 5, 6],
        },
      }),
      Underline,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          target: '_blank',
          rel: 'noopener noreferrer',
        },
      }),
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      TextStyle,
      Color,
      // TODO: FontFamily extension disabled due to compatibility issues
      // FontFamily.configure({
      //   types: ['textStyle'],
      // }),
      Placeholder.configure({
        placeholder: customField.Placeholder || 'Enter text...',
      }),
    ],
    content: customField.Value ? String(customField.Value) : '',
    editable: canEdit && customField.ConversationIsEnable,
    onUpdate: ({ editor }) =>
    {
      const html = editor.getHTML();
      handleDynamicInputChange(customField.CustomFieldId, html);
    },
    onBlur: () =>
    {
      if (UpdateCustomFields)
      {
        UpdateCustomFields(customField);
      }
    },
  });

  // Update editor content when customField.Value changes
  useEffect(() =>
  {
    if (editor && customField.Value !== editor.getHTML())
    {
      const value = customField.Value ? String(customField.Value) : '';
      editor.commands.setContent(value);
    }
  }, [customField.Value, editor]);

  // Update editor editable state
  useEffect(() =>
  {
    if (editor)
    {
      editor.setEditable(canEdit && customField.ConversationIsEnable);
    }
  }, [canEdit, customField.ConversationIsEnable, editor]);

  if (!editor)
  {
    return null;
  }

  const handleSetLink = () =>
  {
    const url = window.prompt('Enter URL:');
    if (url)
    {
      editor.chain().focus().setLink({ href: url }).run();
    }
  };

  const handleSetColor = (color: string) =>
  {
    editor.chain().focus().setColor(color).run();
    setCurrentColor(color);
    setShowColorPicker(false);
  };

  const handleFontSizeChange = (e: React.ChangeEvent<HTMLSelectElement>) =>
  {
    const size = e.target.value;
    if (size === 'normal')
    {
      editor.chain().focus().setParagraph().run();
    }
    else
    {
      const level = parseInt(size) as 1 | 2 | 3 | 4 | 5 | 6;
      editor.chain().focus().setHeading({ level }).run();
    }
  };

  const handleFontFamilyChange = (e: React.ChangeEvent<HTMLSelectElement>) =>
  {
    const font = e.target.value;
    if (font)
    {
      editor.chain().focus().setFontFamily(font).run();
    }
    else
    {
      editor.chain().focus().unsetFontFamily().run();
    }
  };

  const isFieldDisabled = !canEdit || !customField.ConversationIsEnable;

  const colors = [
    '#000000',
    '#444444',
    '#666666',
    '#999999',
    '#CCCCCC',
    '#EEEEEE',
    '#F3F3F3',
    '#FFFFFF',
    '#FF0000',
    '#FF9900',
    '#FFFF00',
    '#00FF00',
    '#00FFFF',
    '#0000FF',
    '#9900FF',
    '#FF00FF',
    '#F4CCCC',
    '#FCE5CD',
    '#FFF2CC',
    '#D9EAD3',
    '#D0E0E3',
    '#CFE2F3',
    '#D9D2E9',
    '#EAD1DC',
    '#EA9999',
    '#F9CB9C',
    '#FFE599',
    '#B6D7A8',
    '#A2C4C9',
    '#9FC5E8',
    '#B4A7D6',
    '#D5A6BD',
    '#E06666',
    '#F6B26B',
    '#FFD966',
    '#93C47D',
    '#76A5AF',
    '#6FA8DC',
    '#8E7CC3',
    '#C27BA0',
    '#CC0000',
    '#E69138',
    '#F1C232',
    '#6AA84F',
    '#45818E',
    '#3D85C6',
    '#674EA7',
    '#A64D79',
    '#990000',
    '#B45F06',
    '#BF9000',
    '#38761D',
    '#134F5C',
    '#0B5394',
    '#351C75',
    '#741B47',
    '#660000',
    '#783F04',
    '#7F6000',
    '#274E13',
    '#0C343D',
    '#073763',
    '#20124D',
    '#4C1130',
  ];

  return (
    <div className={`corgee-rich-text-editor${showRequiredError ? ' submit-required-error' : ''}`}>
      <div className="editor-toolbar">
        {/* Style buttons */}
        <div className="toolbar-group">
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleBold().run()}
            className={editor.isActive('bold') ? 'is-active' : ''}
            disabled={isFieldDisabled}
            title="Bold"
          >
            <strong>B</strong>
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleItalic().run()}
            className={editor.isActive('italic') ? 'is-active' : ''}
            disabled={isFieldDisabled}
            title="Italic"
          >
            <em>I</em>
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleUnderline().run()}
            className={editor.isActive('underline') ? 'is-active' : ''}
            disabled={isFieldDisabled}
            title="Underline"
          >
            <u>U</u>
          </button>
        </div>

        {/* Font family */}
        <div className="toolbar-group">
          <select onChange={handleFontFamilyChange} disabled={isFieldDisabled} title="Font Family">
            <option value="">Default</option>
            <option value="Arial">Arial</option>
            <option value="Arial Black">Arial Black</option>
            <option value="Comic Sans MS">Comic Sans MS</option>
            <option value="Roboto">Roboto</option>
            <option value="Courier New">Courier New</option>
            <option value="Helvetica">Helvetica</option>
          </select>
        </div>

        {/* Font size */}
        <div className="toolbar-group">
          <select onChange={handleFontSizeChange} disabled={isFieldDisabled} title="Font Size">
            <option value="normal">Normal</option>
            <option value="1">Heading 1</option>
            <option value="2">Heading 2</option>
            <option value="3">Heading 3</option>
            <option value="4">Heading 4</option>
            <option value="5">Heading 5</option>
            <option value="6">Heading 6</option>
          </select>
        </div>

        {/* Color picker */}
        <div className="toolbar-group">
          <div className="toolbar-dropdown">
            <button
              type="button"
              className="color-picker-button"
              onClick={() => setShowColorPicker(!showColorPicker)}
              disabled={isFieldDisabled}
              title="Text Color"
            >
              A<span className="color-indicator" style={{ backgroundColor: currentColor }} />
            </button>
            {showColorPicker && (
              <div className="dropdown-content show" style={{ minWidth: '200px', padding: '5px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(8, 1fr)', gap: '2px' }}>
                  {colors.map(color => (
                    <button
                      key={color}
                      type="button"
                      style={{
                        backgroundColor: color,
                        width: '20px',
                        height: '20px',
                        border: '1px solid #ccc',
                        cursor: 'pointer',
                        padding: 0,
                        minWidth: 'auto',
                      }}
                      onClick={() => handleSetColor(color)}
                      title={color}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Alignment and lists */}
        <div className="toolbar-group">
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            className={editor.isActive('bulletList') ? 'is-active' : ''}
            disabled={isFieldDisabled}
            title="Bullet List"
          >
            • List
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            className={editor.isActive('orderedList') ? 'is-active' : ''}
            disabled={isFieldDisabled}
            title="Numbered List"
          >
            1. List
          </button>
        </div>

        {/* Insert options */}
        <div className="toolbar-group">
          <button
            type="button"
            onClick={handleSetLink}
            className={editor.isActive('link') ? 'is-active' : ''}
            disabled={isFieldDisabled}
            title="Insert Link"
          >
            🔗
          </button>
        </div>
      </div>

      <div className="editor-content">
        <EditorContent editor={editor} />
      </div>
    </div>
  );
};

export default RichTextEditor;
